import classnames from "classnames";
import $ from "jquery";
import React, { useCallback, useEffect, useRef, useState } from "react";
import server from "../../../config/server";
import Moment from "../../../utils/Moment";
import { smallBox } from "../../../utils/message";
import { Notification } from "./Notification";
import { Task } from "./Task";

const Components = {
  task: Task,
  notification: Notification,
};

const buildRecipientText = (task) => {
  return [task?.to, task?.recipientName]
    .map((value) => (value || "").trim())
    .filter(Boolean)
    .join(" - ");
};

const buildFailedMessage = (task) => {
  const errorMessage = (task?.errorMessage || "").trim();
  if (errorMessage) {
    return errorMessage;
  }
  return `Mail failed to ${buildRecipientText(task) || ""}${task?.subject ? ` - ${task.subject}` : ""}`;
};

const buildTaskActivities = (status) => {
  const activeBatch = status?.activeBatch;
  if (!activeBatch) {
    return [];
  }
  const currentRecipient = buildRecipientText({
    recipientName: activeBatch.currentRecipientName,
    to: activeBatch.currentTo,
  });
  return [
    {
      type: "task",
      status: (status.failedMails || []).length > 0 ? "PRIMARY" : "NORMAL",
      title: `Send mail (${activeBatch.completedCount}/${activeBatch.totalCount})`,
      description: activeBatch.state !== "COMPLETED" && currentRecipient ? `Đang gửi: ${currentRecipient}` : "",
      completed: activeBatch.state === "COMPLETED",
      succeededCount: activeBatch.succeededCount,
      failedCount: activeBatch.failedCount,
      width: activeBatch.progressPercent,
      progressClass: "progress-sm",
    },
  ];
};

const buildNotificationActivities = (status) => {
  if (!status) {
    return [];
  }
  return (status.failedMails || []).map((task) => ({
    type: "notification",
    icon: "fa-warning",
    failedMailId: task.failedMailId,
    to: task.to,
    message: buildFailedMessage(task),
    time: task.finishedAt || "",
  }));
};

const buildActivities = (status) => [
  {
    name: "task",
    title: "Tasks",
    data: buildTaskActivities(status),
  },
  {
    name: "notification",
    title: "Notify",
    data: buildNotificationActivities(status),
  },
];

const getActionErrorMessage = (error) => {
  return error?.response?.data?.message || error?.message || "Thao tác thất bại";
};

const ensureActionSucceeded = (response) => {
  const status = Number(response?.data?.status);
  if (!Number.isFinite(status) || status < 200 || status >= 300) {
    throw new Error(response?.data?.message || "Thao tác thất bại");
  }
  return response;
};

const showActionSuccess = (message) => {
  smallBox({
    title: "Thành công!",
    content: message || "",
    color: "#739E73",
    iconSmall: "fa fa-check fa-2x fadeInRight animated",
    timeout: 5000,
  });
};

const showActionError = (message) => {
  smallBox({
    title: "Thất bại!",
    content: message || "",
    color: "#C46A69",
    iconSmall: "fa fa-times fa-2x fadeInRight animated",
    timeout: 5000,
  });
};

const Activities = () => {
  const [activity, setActivity] = useState({ data: [] });
  const [activities, setActivities] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [active, setActive] = useState(false);

  const dropdownRef = useRef(null);
  const dropdownToggleRef = useRef(null);
  const loadingTextRef = useRef(null);
  const loadingSpinRef = useRef(null);

  const fetchActivities = useCallback(() => {
    return server.get("/app/company/mail/status").then((response) => {
      const data = buildActivities(response.data && response.data.response);
      setActivities(data);
      setActivity(data[0]);
      setLastUpdate(new Date());
    });
  }, []);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const toggleDropdown = (e) => {
    e.preventDefault();
    const $dropdown = $(dropdownRef.current);
    const $dropdownToggle = $(dropdownToggleRef.current);

    if (active) {
      $dropdown.fadeOut(150);
    } else {
      $dropdown.fadeIn(150);
      fetchActivities();
    }

    setActive(!active);
    $dropdownToggle.toggleClass("active", !active);
  };

  useEffect(() => {
    const handleMouseDown = (event) => {
      if (!active) {
        return;
      }
      const dropdown = dropdownRef.current;
      const dropdownToggle = dropdownToggleRef.current;
      if ((dropdown && dropdown.contains(event.target)) || (dropdownToggle && dropdownToggle.contains(event.target))) {
        return;
      }
      $(dropdown).fadeOut(150);
      $(dropdownToggle).removeClass("active");
      setActive(false);
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [active]);

  const handleSendFailedNotification = useCallback(
    (item) => {
      return server
        .post("/app/company/mail/failed/send", { failedMailId: item.failedMailId })
        .then(ensureActionSucceeded)
        .then(() => showActionSuccess("Đã đưa email vào hàng đợi gửi lại"))
        .catch((error) => showActionError(getActionErrorMessage(error)))
        .finally(() => fetchActivities().catch((error) => showActionError(getActionErrorMessage(error))));
    },
    [fetchActivities]
  );

  const handleDeleteFailedNotification = useCallback(
    (item) => {
      return server
        .delete("/app/company/mail/failed", { data: { failedMailId: item.failedMailId } })
        .then(ensureActionSucceeded)
        .then(() => showActionSuccess("Đã xóa thông báo email lỗi"))
        .catch((error) => showActionError(getActionErrorMessage(error)))
        .finally(() => fetchActivities().catch((error) => showActionError(getActionErrorMessage(error))));
    },
    [fetchActivities]
  );

  const handleClearCompletedBatch = useCallback(() => {
    return server
      .delete("/app/company/mail/completed")
      .then(ensureActionSucceeded)
      .then(() => showActionSuccess("Đã xác nhận đợt gửi email"))
      .catch((error) => showActionError(getActionErrorMessage(error)))
      .finally(() => fetchActivities().catch((error) => showActionError(getActionErrorMessage(error))));
  }, [fetchActivities]);

  const update = () => {
    $(loadingTextRef.current).html("Loading...");
    $(loadingSpinRef.current).addClass("fa-spin");
    fetchActivities()
      .catch((error) => showActionError(getActionErrorMessage(error)))
      .finally(() => {
        $(loadingTextRef.current).html("");
        $(loadingSpinRef.current).removeClass("fa-spin");
      });
  };

  const count = activities.reduce((sum, a) => sum + a.data.length, 0);

  return (
    <div>
      <span id="activity" onClick={toggleDropdown} ref={dropdownToggleRef} className="activity-dropdown">
        <i className="fa fa-user" />
        <b className="badge bg-color-red">{count}</b>
      </span>
      <div className="ajax-dropdown" ref={dropdownRef}>
        <div className="btn-group btn-group-justified" data-toggle="buttons">
          {activities.map((_activity, idx) => (
            <label
              className={classnames([
                "btn",
                "btn-default",
                {
                  active: _activity.name === activity.name,
                },
              ])}
              key={idx}
              onClick={() => setActivity(_activity)}
            >
              <input type="radio" name="activity" />
              {_activity.title} ({_activity.data.length})
            </label>
          ))}
        </div>

        <div className="ajax-notifications custom-scroll">
          <ul className="notification-body">
            {activity.data.map((item, idx) => {
              const Component = Components[item.type];
              return (
                <li key={idx}>
                  {React.createElement(Component, {
                    item,
                    lastUpdated: lastUpdate,
                    onSendFailedNotification: handleSendFailedNotification,
                    onDeleteFailedNotification: handleDeleteFailedNotification,
                    onClearCompletedBatch: handleClearCompletedBatch,
                  })}
                </li>
              );
            })}
          </ul>
        </div>

        <span>
          Last updated on:
          <Moment date={lastUpdate} format="h:mm:ss a" />
          <button type="button" onClick={update} className="btn btn-xs btn-default pull-right">
            <i ref={loadingSpinRef} className="fa fa-refresh" />
            <span ref={loadingTextRef} />
          </button>
        </span>
      </div>
    </div>
  );
};

export default Activities;
